/**
 * Recommendation Generator — Client-Side Heuristic Only
 *
 * Generates actionable improvement suggestions for founders based
 * on heuristic scoring results. These are general improvement tips,
 * NOT the proprietary investor methodology.
 *
 * Investor-facing scoring and recommendations are computed server-side
 * via the `ai-score` edge function (`runScoring()` → `score_event`).
 */

import type { StartupProfile } from '@/lib/state/profile-store';
import type { AIRecommendation } from './types';
import type { FundingStage } from '@/lib/dataroom/types';
import {
  type ScoringDimension,
  type DimensionScore,
  type StartupScore,
  STAGE_WEIGHTS,
  getDimensionDefinition,
} from './methodology';

// =============================================================================
// RECOMMENDATION TEMPLATES
// =============================================================================

interface RecommendationTemplate {
  low: string; // Score 0-40
  medium: string; // Score 40-70
  high: string; // Score 70-100
  actionItems: {
    low: string[];
    medium: string[];
    high: string[];
  };
}

const DIMENSION_RECOMMENDATIONS: Record<ScoringDimension, RecommendationTemplate> = {
  team: {
    low: 'Critical team gaps need immediate attention. Focus on recruiting key roles and documenting founder experience.',
    medium: 'Team foundation is good but could be strengthened. Consider adding key hires or advisory board members.',
    high: 'Strong team composition. Maintain culture and continue building bench strength.',
    actionItems: {
      low: [
        'Create detailed founder bios highlighting relevant experience',
        'Identify and prioritize critical hiring needs (CTO, VP Sales, etc.)',
        'Build an advisory board with industry experts',
        'Document team org chart and reporting structure',
      ],
      medium: [
        'Fill remaining key role gaps within 3 months',
        'Strengthen domain expertise documentation',
        'Establish formal performance review processes',
      ],
      high: [
        'Continue developing talent pipeline',
        'Document team culture and hiring practices',
      ],
    },
  },
  product: {
    low: 'Product is at early stage. Focus on reaching MVP and getting initial user feedback.',
    medium: 'Product shows promise. Prioritize user acquisition and feature iteration based on feedback.',
    high: 'Strong product foundation. Focus on scale and polish.',
    actionItems: {
      low: [
        'Define MVP scope with core value proposition',
        'Get 10 beta users for initial feedback',
        'Create product demo video for investors',
        'Document technical architecture',
      ],
      medium: [
        'Gather NPS scores and user testimonials',
        'Create 12-month product roadmap',
        'Improve UX based on user feedback',
      ],
      high: [
        'Scale product infrastructure for growth',
        'Document product differentiation clearly',
      ],
    },
  },
  market: {
    low: 'Market opportunity needs better definition. Research and document TAM/SAM/SOM.',
    medium: 'Market understanding is decent. Strengthen positioning and target segment clarity.',
    high: 'Excellent market opportunity. Focus on execution to capture share.',
    actionItems: {
      low: [
        'Research and document total addressable market (TAM)',
        'Define serviceable market (SAM) with clear boundaries',
        'Identify 3-5 direct competitors and positioning',
        'Create ideal customer profile (ICP)',
      ],
      medium: [
        'Include third-party market research in data room',
        'Sharpen positioning vs competitors',
        'Document market growth trends and tailwinds',
      ],
      high: [
        'Monitor market shifts and maintain leadership position',
        'Explore adjacent market opportunities',
      ],
    },
  },
  traction: {
    low: 'Traction is limited. Focus intensely on customer acquisition and revenue generation.',
    medium: 'Showing good momentum. Accelerate growth and improve retention metrics.',
    high: 'Strong traction signals. Document metrics clearly and maintain growth trajectory.',
    actionItems: {
      low: [
        'Get first 10 paying customers',
        'Implement tracking for key metrics (MRR, users, churn)',
        'Run customer interviews to validate demand',
        'Create case studies from early adopters',
      ],
      medium: [
        'Increase MoM growth to 15%+',
        'Improve retention to 90%+',
        'Document cohort analysis',
        'Implement referral program',
      ],
      high: [
        'Maintain growth while improving unit economics',
        'Document PMF signals clearly',
      ],
    },
  },
  defensibility: {
    low: 'Limited defensibility. Identify and build competitive moats.',
    medium: 'Some defensive advantages emerging. Strengthen and document.',
    high: 'Strong moat established. Maintain and extend advantages.',
    actionItems: {
      low: [
        'Identify potential IP that can be protected',
        'Explore data network effects opportunities',
        'Deepen customer integrations',
        'Build switching costs into product',
      ],
      medium: [
        'File provisional patents for key innovations',
        'Document proprietary technology advantages',
        'Strengthen brand recognition',
      ],
      high: [
        'Continue investing in R&D moat',
        'Monitor and respond to competitive threats',
      ],
    },
  },
  competition: {
    low: 'Competitive position is weak. Differentiate clearly or pivot.',
    medium: 'Competitive position is manageable. Strengthen differentiation.',
    high: 'Strong competitive position. Maintain advantages and watch for new entrants.',
    actionItems: {
      low: [
        'Create detailed competitive landscape analysis',
        'Identify unique differentiators (10x better on something)',
        'Consider repositioning to less crowded niche',
        'Build competitive battle cards',
      ],
      medium: [
        'Quantify advantages vs top competitors',
        'Monitor competitor moves closely',
        'Document win/loss reasons',
      ],
      high: [
        'Maintain competitive intelligence program',
        'Continue innovating to stay ahead',
      ],
    },
  },
  'business-model': {
    low: 'Business model unproven. Validate revenue model quickly.',
    medium: 'Business model emerging. Optimize unit economics.',
    high: 'Strong business model. Scale with confidence.',
    actionItems: {
      low: [
        'Define clear pricing strategy and test it',
        'Get first paying customers to validate model',
        'Calculate basic unit economics',
        'Create financial model projections',
      ],
      medium: [
        'Improve LTV:CAC to 3x+',
        'Reduce payback period to under 12 months',
        'Document gross margins by customer segment',
      ],
      high: [
        'Optimize pricing for expansion revenue',
        'Document path to profitability clearly',
      ],
    },
  },
  timing: {
    low: 'Timing rationale unclear. Articulate why now is the right time.',
    medium: 'Good timing signals. Document catalysts more clearly.',
    high: 'Excellent timing. Capitalize on the moment.',
    actionItems: {
      low: [
        'Identify specific catalyst enabling this opportunity',
        'Document market shifts and trend data',
        'Explain why this couldn\'t work 2 years ago',
        'Show why waiting would be wrong',
      ],
      medium: [
        'Include third-party trend data in pitch',
        'Document macro tailwinds supporting growth',
      ],
      high: [
        'Move quickly to capitalize on timing window',
        'Document timing thesis clearly',
      ],
    },
  },
  'vision-story': {
    low: 'Vision and narrative need significant work. Refine your story.',
    medium: 'Vision is clear but could be more compelling. Strengthen narrative.',
    high: 'Compelling vision and story. Use it to inspire and attract.',
    actionItems: {
      low: [
        'Write a clear, ambitious vision statement',
        'Craft elevator pitch (30 seconds)',
        'Work with pitch coach on narrative',
        'Create compelling pitch deck',
      ],
      medium: [
        'Add customer success stories to narrative',
        'Practice pitch delivery',
        'Refine competitive framing',
      ],
      high: [
        'Maintain consistent storytelling across channels',
        'Update narrative as company evolves',
      ],
    },
  },
  'gtm-execution': {
    low: 'GTM strategy needs development. Define and test acquisition channels.',
    medium: 'GTM showing early results. Scale what works.',
    high: 'Strong GTM execution. Maintain and optimize.',
    actionItems: {
      low: [
        'Define go-to-market strategy document',
        'Identify 3 potential acquisition channels',
        'Test founder-led sales process',
        'Set up basic marketing infrastructure',
      ],
      medium: [
        'Double down on best-performing channel',
        'Build repeatable sales playbook',
        'Invest in brand building',
      ],
      high: [
        'Scale GTM team and processes',
        'Document customer acquisition cost by channel',
      ],
    },
  },
  'financial-health': {
    low: 'Financial position concerning. Extend runway or reduce burn.',
    medium: 'Financial health adequate. Optimize capital efficiency.',
    high: 'Strong financial position. Deploy capital strategically.',
    actionItems: {
      low: [
        'Calculate and extend runway to 12+ months',
        'Reduce non-essential burn immediately',
        'Set up proper financial reporting',
        'Consider bridge financing if needed',
      ],
      medium: [
        'Improve capital efficiency metrics',
        'Implement monthly financial reviews',
        'Build financial forecasting model',
      ],
      high: [
        'Maintain financial discipline while growing',
        'Consider strategic capital deployment',
      ],
    },
  },
  'execution-velocity': {
    low: 'Execution pace too slow. Identify and remove blockers.',
    medium: 'Good execution pace. Continue momentum.',
    high: 'Excellent execution velocity. Maintain while scaling.',
    actionItems: {
      low: [
        'Identify top 3 execution blockers',
        'Set clear weekly/monthly milestones',
        'Implement agile development practices',
        'Create public changelog to show progress',
      ],
      medium: [
        'Increase shipping velocity',
        'Document milestone achievement track record',
      ],
      high: [
        'Maintain velocity while scaling team',
        'Celebrate and share wins publicly',
      ],
    },
  },
  scalability: {
    low: 'Scalability concerns present. Address before growth push.',
    medium: 'Scalability adequate for current stage. Plan for growth.',
    high: 'Strong scalability foundation. Ready for growth.',
    actionItems: {
      low: [
        'Conduct architecture review for scale',
        'Identify operational bottlenecks',
        'Create hiring plan for growth',
        'Automate manual processes',
      ],
      medium: [
        'Complete load testing',
        'Document operational procedures',
        'Build hiring pipeline',
      ],
      high: [
        'Continue investing in scalable infrastructure',
        'Maintain operational excellence',
      ],
    },
  },
  'deal-attractiveness': {
    low: 'Deal terms need attention. Work on investor-friendly structure.',
    medium: 'Deal terms are reasonable. Consider optimizing for better alignment.',
    high: 'Attractive deal structure. Maintain investor-friendly positioning.',
    actionItems: {
      low: [
        'Benchmark your terms against similar stage companies',
        'Work with a lawyer on investor-friendly term sheet',
        'Research exit comparables in your sector',
        'Prepare a clear cap table summary',
      ],
      medium: [
        'Optimize term sheet for current market conditions',
        'Document exit pathways and comparables',
        'Prepare investor-ready financial model',
      ],
      high: [
        'Maintain competitive terms',
        'Keep exit analysis updated',
      ],
    },
  },
  'social-proof': {
    low: 'Limited social proof. Build credibility through investors, accelerators, or partnerships.',
    medium: 'Some social proof established. Continue building external validation.',
    high: 'Strong social proof. Leverage it in fundraising narrative.',
    actionItems: {
      low: [
        'Apply to relevant accelerator programs',
        'Enter startup competitions in your domain',
        'Seek strategic advisors or angel investors',
        'Build partnerships with recognized organizations',
      ],
      medium: [
        'Highlight existing investor or accelerator credentials',
        'Pursue strategic partnerships for credibility',
        'Document press coverage and awards',
      ],
      high: [
        'Leverage social proof in fundraising materials',
        'Continue building high-profile relationships',
      ],
    },
  },
};

// =============================================================================
// RECOMMENDATION FUNCTIONS
// =============================================================================

/**
 * Get score tier for recommendation selection
 */
function getScoreTier(score: number): 'low' | 'medium' | 'high' {
  if (score < 40) return 'low';
  if (score < 70) return 'medium';
  return 'high';
}

/**
 * Generate recommendation for a single dimension
 */
export function generateDimensionRecommendation(
  dimension: ScoringDimension,
  score: number,
  subScores: { criterionId: string; score: number; feedback: string }[],
  profile: StartupProfile
): string {
  const template = DIMENSION_RECOMMENDATIONS[dimension];
  const tier = getScoreTier(score);

  // Base recommendation from template
  let recommendation = template[tier];

  // Find lowest sub-criterion for specific guidance
  if (subScores.length > 0) {
    const lowestSub = subScores.reduce(
      (lowest, current) => (current.score < lowest.score ? current : lowest),
      subScores[0]
    );

    // Add specific guidance if available
    if (lowestSub.feedback && lowestSub.score < 6) {
      recommendation += ` Focus area: ${lowestSub.feedback}`;
    }
  }

  // Add context-specific suggestions
  if (dimension === 'traction' && profile.stage === 'seed' && (profile.monthlyRevenue ?? 0) < 10000) {
    recommendation += ' For seed stage, aim for $10K+ MRR.';
  }

  if (dimension === 'team' && (profile.teamSize ?? 1) <= 2 && profile.stage !== 'pre-seed') {
    recommendation += ' Consider expanding the founding team.';
  }

  return recommendation;
}

/**
 * Generate top recommendations sorted by priority
 */
export function generateTopRecommendations(
  startupScore: StartupScore,
  profile: StartupProfile,
  count: number = 3
): AIRecommendation[] {
  const stage = startupScore.stage;
  const weights = STAGE_WEIGHTS[stage];

  // Score each dimension by improvement potential
  const dimensionPriorities = startupScore.dimensionScores.map((ds) => {
    const weight = weights?.[ds.dimension] ?? 0.08;
    const improvementPotential = (100 - ds.rawScore) * weight;
    const tier = getScoreTier(ds.rawScore);

    return {
      dimension: ds.dimension,
      score: ds.rawScore,
      weight,
      improvementPotential,
      tier,
      subScores: ds.subScores,
    };
  });

  // Sort by improvement potential (highest first)
  dimensionPriorities.sort((a, b) => b.improvementPotential - a.improvementPotential);

  // Take top N and generate full recommendations
  const recommendations: AIRecommendation[] = [];

  for (let i = 0; i < Math.min(count, dimensionPriorities.length); i++) {
    const dp = dimensionPriorities[i];
    const template = DIMENSION_RECOMMENDATIONS[dp.dimension];
    const definition = getDimensionDefinition(dp.dimension);
    const tier = dp.tier;

    // Determine priority based on tier and weight
    let priority: 'high' | 'medium' | 'low' = 'medium';
    if (tier === 'low' && dp.weight >= 0.1) {
      priority = 'high';
    } else if (tier === 'high' || dp.weight < 0.05) {
      priority = 'low';
    }

    // Calculate potential score (optimistic improvement)
    const potentialImprovement = tier === 'low' ? 30 : tier === 'medium' ? 20 : 10;
    const potentialScore = Math.min(100, dp.score + potentialImprovement);

    // Get suggestion and action items
    const suggestion = generateDimensionRecommendation(
      dp.dimension,
      dp.score,
      dp.subScores,
      profile
    );
    const actionItems = template.actionItems[tier].slice(0, 4);

    recommendations.push({
      id: `rec-${dp.dimension}-${Date.now()}-${i}`,
      area: definition?.name ?? dp.dimension,
      priority,
      currentScore: Math.round(dp.score),
      potentialScore: Math.round(potentialScore),
      suggestion,
      actionItems,
    });
  }

  return recommendations;
}

/**
 * Generate quick win recommendations (high impact, lower effort)
 */
export function generateQuickWins(
  startupScore: StartupScore,
  profile: StartupProfile,
  count: number = 3
): AIRecommendation[] {
  const stage = startupScore.stage;
  const weights = STAGE_WEIGHTS[stage];

  // Quick wins = dimensions with medium scores (40-70) and high weights
  // These have the best effort-to-impact ratio
  const quickWinCandidates = startupScore.dimensionScores
    .filter((ds) => ds.rawScore >= 40 && ds.rawScore < 70)
    .map((ds) => ({
      dimension: ds.dimension,
      score: ds.rawScore,
      weight: weights?.[ds.dimension] ?? 0.08,
      subScores: ds.subScores,
    }))
    .sort((a, b) => b.weight - a.weight);

  // If not enough medium-score dimensions, include low-score ones with high weights
  if (quickWinCandidates.length < count) {
    const lowScoreCandidates = startupScore.dimensionScores
      .filter((ds) => ds.rawScore < 40)
      .map((ds) => ({
        dimension: ds.dimension,
        score: ds.rawScore,
        weight: weights?.[ds.dimension] ?? 0.08,
        subScores: ds.subScores,
      }))
      .sort((a, b) => b.weight - a.weight);

    quickWinCandidates.push(...lowScoreCandidates);
  }

  const recommendations: AIRecommendation[] = [];

  for (let i = 0; i < Math.min(count, quickWinCandidates.length); i++) {
    const qw = quickWinCandidates[i];
    const template = DIMENSION_RECOMMENDATIONS[qw.dimension];
    const definition = getDimensionDefinition(qw.dimension);
    const tier = getScoreTier(qw.score);

    // Quick wins are generally medium priority
    const priority = tier === 'low' ? 'high' : 'medium';

    const potentialImprovement = 15;
    const potentialScore = Math.min(100, qw.score + potentialImprovement);

    const suggestion = `Quick win opportunity: ${template.medium}`;
    const actionItems = template.actionItems[tier].slice(0, 3);

    recommendations.push({
      id: `qw-${qw.dimension}-${Date.now()}-${i}`,
      area: definition?.name ?? qw.dimension,
      priority,
      currentScore: Math.round(qw.score),
      potentialScore: Math.round(potentialScore),
      suggestion,
      actionItems,
    });
  }

  return recommendations;
}

/**
 * Generate recommendations for all dimensions
 */
export function generateAllRecommendations(
  startupScore: StartupScore,
  profile: StartupProfile
): Map<ScoringDimension, string> {
  const recommendations = new Map<ScoringDimension, string>();

  for (const ds of startupScore.dimensionScores) {
    const recommendation = generateDimensionRecommendation(
      ds.dimension,
      ds.rawScore,
      ds.subScores,
      profile
    );
    recommendations.set(ds.dimension, recommendation);
  }

  return recommendations;
}
